// react
import { createSlice, isRejected, type PayloadAction } from '@reduxjs/toolkit';
import { formatFullName } from '@karehero/helpers/name';
import iamApi from 'store/api/iam';
import { accountApi } from 'store/api/account';

// models
import { Account } from '@karehero/models';

// sentry
import * as Sentry from '@sentry/react';

// capacitor
import { Capacitor } from '@capacitor/core';

const redirectDestination = Capacitor.isNativePlatform()
  ? 'karehero://'
  : `${window.location.origin}/`;

export type KindeAccount = {
  familyName: string;
  givenName: string;
  name: string;
  email: string;
  picture: string;
};

export interface IamState {
  account: Account | null;
  kindeAccount: KindeAccount | null;
  isNewKindeAccount: boolean;
  /**
   * Set from sign-out until the page next loads or the account signs in.
   * Signing out clears the account and Kinde's session separately, so this
   * stops the app signing the account straight back in between the two.
   */
  isSigningOut: boolean;
}

export const iamSlice = createSlice({
  name: 'iam',
  initialState: {
    kindeAccount: null,
    account: null,
    isNewKindeAccount: false,
    isSigningOut: false,
  } as IamState,
  reducers: {
    setKindeAccount: (state, { payload }) => {
      state.kindeAccount = payload;
    },
    invalidateAccount: (state) => {
      state.account = null;
    },
    /** The name the API saved, as its event says; nothing else reloads. */
    accountNameUpdated: (
      state,
      {
        payload,
      }: PayloadAction<{
        accountID: string;
        firstName: string;
        lastName: string;
      }>,
    ) => {
      if (state.account?.id !== payload.accountID) return;
      state.account.firstName = payload.firstName;
      state.account.lastName = payload.lastName;
      state.account.fullName = formatFullName(
        payload.firstName,
        payload.lastName,
      );
    },
    clearAuth: (state) => {
      state.account = null;
      state.kindeAccount = null;
      state.isNewKindeAccount = false;
    },
    clearIsNewKindeAccount: (state) => {
      state.isNewKindeAccount = false;
    },
    setIsSigningOut: (state, { payload }: PayloadAction<boolean>) => {
      state.isSigningOut = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(isRejected(), (state, action) => {
        const status = (action.payload as any)?.status;
        if (status === 401) {
          // ENG-3071: Track when a 401 clears auth state. This is the broadest
          // logout trigger — ANY rejected RTK Query action with 401 wipes the
          // account. Logging the endpoint helps identify which API call caused it.
          const endpoint = (action.meta as any)?.arg?.endpointName ?? 'unknown';
          Sentry.addBreadcrumb({
            category: 'auth',
            message: `401 response cleared auth state`,
            level: 'warning',
            data: { endpoint, status },
          });
          state.account = null;
        }
      })
      .addMatcher(
        iamApi.endpoints.signInKinde.matchFulfilled,
        (state, { payload }) => {
          state.account = payload;
          state.isNewKindeAccount = payload.isNewKindeAccount || false;
          state.isSigningOut = false;
        },
      )
      .addMatcher(
        iamApi.endpoints.registerMemberByInviteKey.matchFulfilled,
        (state) => {
          if (state.account) {
            state.account = null;
          }
        },
      )
      .addMatcher(
        iamApi.endpoints.registerMemberByInviteMagicLink.matchFulfilled,
        (state) => {
          if (state.account) {
            state.account = null;
          }
        },
      )
      .addMatcher(
        iamApi.endpoints.signInKinde.matchRejected,
        (state, action) => {
          // ENG-3071: signInKinde specifically rejected — this triggers a hard
          // redirect to Kinde logout. This is the most disruptive logout path.
          const status = (action.payload as any)?.status;
          Sentry.captureMessage('auth.forced_logout: signInKinde rejected', {
            level: 'warning',
            tags: {
              'auth.trigger': 'signInKinde.rejected',
              'auth.status': String(status ?? 'unknown'),
              'auth.platform': Capacitor.getPlatform(),
            },
          });

          if (Capacitor.isNativePlatform()) {
            // On native, redirect to in-app sign-in so the main WebView navigates
            // to our origin. Redirecting to Kinde logout would be intercepted by
            // the native bridge and opened in the InAppBrowser, causing a second
            // IAB and white screen (see registration flow after 0.44.0).
            window.location.href = `${window.location.origin}/sign-in`;
            return;
          }

          let kindeLogoutUrl = new URL(
            `${import.meta.env.REACT_APP_KINDE_TOKEN_DOMAIN}/logout`,
          );
          const queryParams = new URLSearchParams({
            redirect: `${redirectDestination}sign-in`,
          });
          kindeLogoutUrl.search = queryParams.toString();

          window.location.href = kindeLogoutUrl.toString();
        },
      )
      .addMatcher(iamApi.endpoints.signOut.matchFulfilled, (state) => {
        state.account = null;
        state.isNewKindeAccount = false;
      })
      .addMatcher(
        iamApi.endpoints.updateIsOnboarded.matchFulfilled,
        (state) => {
          if (state.account) state.account.isOnboarded = true;
        },
      )
      .addMatcher(
        accountApi.endpoints.updateAccountAvatar.matchFulfilled,
        (state, { payload }) => {
          if (state.account) {
            state.account.avatarUrl = payload.avatarUrl;
            state.account.avatarConfig = payload.avatarConfig;
          }
        },
      )
      .addMatcher(
        accountApi.endpoints.updateAccountConsent.matchFulfilled,
        (state, { payload }) => {
          if (state.account) {
            state.account.accountConsents = payload.accountConsents;
          }
        },
      );
  },
});

export const {
  clearAuth,
  invalidateAccount,
  accountNameUpdated,
  setKindeAccount,
  clearIsNewKindeAccount,
  setIsSigningOut,
} = iamSlice.actions;

export const getAccount = (state: any) => state.iam.account;
export const getIsOnboarded = (state: any) => state.iam.isOnboarded;
export const getKindeAccount = (state: any) => state.iam.kindeAccount;
export const getIsNewKindeAccount = (state: any) => state.iam.isNewKindeAccount;
export const getIsSigningOut = (state: any) => state.iam.isSigningOut;

export default iamSlice.reducer;
